import {
    classNames,
    EmailKeyboardLayout,
    Keyboard,
    layout,
    PhoneKeyboardLayout,
    PushButton,
    TextInput,
    typography,
    useTypeRef,
    View,
    ViewContent,
    ViewFooter,
    ViewFooterItem,
    ViewHeader,
    ViewHeaderItem
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useMemo, useRef, useState} from "react";
import {stylesheet} from "typestyle";
import {useLocalization} from "../../i18n";

const styles = stylesheet({
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%'
    },
    questionContainer: {
        display: 'inline-block',
        margin: '0px auto'
    },
    buttonsContainer: {
        display: 'flex',
        justifyContent: 'space-between'
    },
    question: {
        textAlign: "center",
        fontSize: "1.75rem",
    },
    label: {
        paddingLeft: "1.5em"
    },
    field: {
        padding: "0.75rem",
        transition: "flex 1s,width 1s",
    },
    flexDirectionRowReverse: {
        flexDirection: "row-reverse"
    },
    submitField: {
        padding: "0.75rem",
        display: "flex",
        alignItems: "end"
    },
    submit: {
        height: "3em"
    },
    keyboardContainer: {
        padding: "0.25rem"
    },
    disabledInput: {
        backgroundColor: "var(--color-background-less-contrast)",
    }
});

export interface ReceiptHandlingViewProps {
    onConfirm: (data: ReceiptData) => void;
    onCancel: () => void;
}

export function ReceiptHandlingView({ onConfirm, onCancel }: ReceiptHandlingViewProps) {
    const [receiptDesired, setReceiptDesired] = useState<boolean | undefined>(undefined);

    const handleReceiptDesiredYes = useCallback(() => {
        setReceiptDesired(true);
    }, []);

    const handleReceiptDataInputConfirm = useCallback((data: ReceiptData) => {
        onConfirm(data);
    }, []);

    const handleReceiptDataInputCancel = useCallback(() => {
      setReceiptDesired(undefined);
    },[]);

    if (receiptDesired === undefined) {
        return <ReceiptDesiredQuestionView onYes={handleReceiptDesiredYes} onNo={onCancel} />
    }
    else if (receiptDesired) {
        return <ReceiptDataInputView onConfirm={handleReceiptDataInputConfirm} onCancel={handleReceiptDataInputCancel} />
    }
    return null;
}

interface ReceiptDesiredQuestionViewProps {
    onYes: () => void;
    onNo: () => void;
}

function ReceiptDesiredQuestionView({ onYes, onNo }: ReceiptDesiredQuestionViewProps) {
    //const strings = useStrings(resources);
    const {t} = useLocalization();

    return (
        <View>
            <ViewHeader>
                <ViewHeaderItem className={layout.paddingMedium}>
                    <div className={typography.title}>
                        {t("Receipt")}
                    </div>
                </ViewHeaderItem>
            </ViewHeader>
            <ViewContent className={layout.paddingMedium}>
                <div className={styles.container}>
                    <div className={styles.questionContainer}>
                        <p className={styles.question} >{t("ReceiptQuestion")}</p>
                        <div className={styles.buttonsContainer}>
                            <PushButton onClick={onNo}>
                                {t("No")}
                            </PushButton>
                            <PushButton onClick={onYes} primary>
                                {t("Yes")}
                            </PushButton>
                        </div>
                    </div>
                </div>
            </ViewContent>
        </View>
    );
}

enum ReceiptField {
    email,
    phone,
}

export interface ReceiptData {
    email?: string;
    phone?: string;
}

export interface ReceiptDataViewProps {
    onConfirm: (receiptData: ReceiptData) => void;
    onCancel: () => void;
}

export function ReceiptDataInputView({ onConfirm, onCancel }: ReceiptDataViewProps) {
    //const strings = useStrings(resources);
    const {t} = useLocalization();
    const [emailType, setEmailType] = useTypeRef();
    const [phoneType, setPhoneType] = useTypeRef();
    const [field, setField] = useState<ReceiptField>(ReceiptField.email);
    const emailRef = useRef<HTMLInputElement>();
    const phoneRef = useRef<HTMLInputElement>();
    const [senderData, setSenderData] = useState<ReceiptData>({});

    const handleEmailFocus = useCallback(() => setField(ReceiptField.email), []);
    const handlePhoneFocus = useCallback(() => setField(ReceiptField.phone), []);

    const handleConfirm = useCallback(() => {
        onConfirm(senderData);
    }, [senderData]);

    const handleEmailChanged = useCallback((email: string) => {
        setSenderData({
            ...senderData,
            email
        });
    }, []);

    const handlePhoneChanged = useCallback((phone: string) => {
        setSenderData({
            ...senderData,
            phone
        });
    }, []);

    const recipientDataValid = useMemo<boolean>(() => {
        if (!senderData)
            return false;
        if (senderData.email && senderData.email.length > 0) {
            return !!senderData.email.match(/^(.+)@(.+)\.(.+)$/)
        }
        if (senderData.phone && senderData.phone.length > 5) {
            return true
        }
        return false;
    }, [senderData]);

    return (
        <View>
            <ViewHeader>
                <ViewHeaderItem className={layout.paddingMedium}>
                    <div className={typography.title}>
                        {t("ReceiptDataTitle")}
                    </div>
                    <div className={typography.description}>
                        {t("ReceiptDataDescription")}
                    </div>
                </ViewHeaderItem>
            </ViewHeader>
            <ViewContent className={layout.paddingMedium}>
                <div className={classNames(layout.horizontal, senderData.phone?.length ? styles.flexDirectionRowReverse : undefined)}>
                    {!senderData.phone?.length && (
                        <div className={classNames(styles.field, senderData.email?.length ? layout.fill : undefined)}>
                            <div className={styles.label}>
                                {t("Email")}
                            </div>
                            <TextInput
                                value={senderData.email ?? ""}
                                error={!!senderData.email?.length && !recipientDataValid}
                                onChange={handleEmailChanged}
                                inputRef={emailRef}
                                onFocus={handleEmailFocus}
                                typeRef={setEmailType}
                                disabled={!!senderData.phone?.length}
                                className={!!senderData.phone?.length ? styles.disabledInput : undefined}
                                outline
                                backspace
                            />
                        </div>
                    )}
                    {!senderData.email?.length && (
                        <div className={classNames(styles.field, senderData.phone?.length ? layout.fill : undefined)}>
                            <div className={styles.label}>
                                {t("Phone")}
                            </div>
                            <TextInput
                                value={senderData.phone ?? ""}
                                error={!!senderData.phone?.length && !recipientDataValid}
                                onChange={handlePhoneChanged}
                                inputRef={phoneRef}
                                onFocus={handlePhoneFocus}
                                typeRef={setPhoneType}
                                disabled={!!senderData.email?.length}
                                className={!!senderData.email?.length ? styles.disabledInput : undefined}
                                outline
                                backspace
                            />
                        </div>
                    )}
                </div>
                <div className={styles.keyboardContainer}>
                    {field == ReceiptField.email && (
                        <Keyboard onType={emailType} layout={EmailKeyboardLayout}/>
                    )}

                    {field == ReceiptField.phone && (
                        <Keyboard onType={phoneType} layout={PhoneKeyboardLayout}/>
                    )}
                </div>
            </ViewContent>
            <ViewFooter className={classNames(layout.spaceBetween, layout.paddingMedium)}>
                <ViewFooterItem>
                    <PushButton onClick={onCancel}>
                        {t("Cancel")}
                    </PushButton>
                </ViewFooterItem>
                <ViewFooterItem>
                    <PushButton onClick={handleConfirm} primary>
                        {t("Next")}
                    </PushButton>
                </ViewFooterItem>
            </ViewFooter>
        </View>
    );
}